/**
 * Shared log-entry builder for API sequence generation UIs.
 *
 * @module ui/components/node-config/buildLogEntries
 *
 * Converts raw AgentEvent streams into renderable log entries with
 * friendly labels, status badges, and endpoint counts. Used by both
 * the node-config ApiSequenceGeneratePanel and the FlowDesigner
 * ApiSequenceEmptyState.
 */

import type { AgentEvent } from '../../../hooks/useApiSequenceGenerationStream';

type LogEntry = {
  key: string;
  icon: 'spinner' | 'success' | 'error' | 'info';
  text: string;
  detail?: string;
  badge?: { label: string; variant: 'positive' | 'negative' | 'neutral' };
};

/** Map tool names to user-friendly display labels */
function getToolDisplayLabel(toolName: string, args?: unknown): string {
  const argObj = (args && typeof args === 'object' ? args : {}) as Record<string, unknown>;
  switch (toolName) {
    case 'search_catalog':
      return `Searching API catalog for "${argObj.query ?? ''}"`;
    case 'get_endpoint_detail':
      return 'Inspecting endpoint details';
    case 'test_step':
      return `Testing ${argObj.method ?? 'request'} endpoint`;
    case 'compose_sequence':
      return 'Composing sequence configuration';
    case 'test_sequence':
      return 'Running full sequence test';
    case 'inspect_request':
      return `Inspecting request #${argObj.requestIndex ?? ''}`;
    case 'finalize_config':
      return 'Configuration ready';
    case 'refresh_credential':
      return 'Refreshing credential';
    default:
      return toolName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

/** Extract rich details from a tool-result event */
function getToolResultInfo(event: AgentEvent): {
  detail?: string;
  badge?: { label: string; variant: 'positive' | 'negative' | 'neutral' };
} {
  const result = (event.result && typeof event.result === 'object' ? event.result : {}) as Record<
    string,
    unknown
  >;

  switch (event.toolName) {
    case 'search_catalog': {
      const total = result.totalFound as number | undefined;
      const endpoints = result.endpoints as Array<{ method: string; pattern: string }> | undefined;
      const preview = endpoints
        ?.slice(0, 3)
        .map((e) => `${e.method} ${e.pattern}`)
        .join(', ');
      return {
        detail: `Found ${total ?? 0} endpoint${total === 1 ? '' : 's'}${preview ? `: ${preview}` : ''}`,
      };
    }
    case 'test_step': {
      const statusCode = result.statusCode as number | undefined;
      const durationMs = result.durationMs as number | undefined;
      const isSuccess = statusCode !== undefined && statusCode >= 200 && statusCode < 300;
      return {
        detail: durationMs ? `${durationMs}ms` : undefined,
        badge: statusCode
          ? {
              label: `${statusCode}`,
              variant: isSuccess ? 'positive' : 'negative',
            }
          : undefined,
      };
    }
    case 'test_sequence': {
      const stepResults = result.stepResults as Array<{ success: boolean }> | undefined;
      if (stepResults) {
        const passed = stepResults.filter((s) => s.success).length;
        return {
          detail: `${passed}/${stepResults.length} steps passed`,
          badge: {
            label: passed === stepResults.length ? 'All passed' : 'Some failed',
            variant: passed === stepResults.length ? 'positive' : 'negative',
          },
        };
      }
      return {};
    }
    case 'compose_sequence': {
      const stepCount = result.stepCount as number | undefined;
      return { detail: stepCount ? `${stepCount} steps composed` : undefined };
    }
    case 'inspect_request': {
      const status = result.status as number | null | undefined;
      return {
        detail: status ? `status ${status}` : undefined,
      };
    }
    case 'finalize_config': {
      if (result.finalized === true) return {};
      const error = typeof result.error === 'string' ? result.error : undefined;
      const missing = Array.isArray(result.missingUrlTemplateSteps)
        ? (result.missingUrlTemplateSteps as string[]).join(', ')
        : undefined;
      const invalidIds = Array.isArray(result.invalidApiRequestIds)
        ? (result.invalidApiRequestIds as Array<{ label: string }>)
            .map((s) => s.label)
            .join(', ')
        : undefined;
      const detail = error ?? (missing
        ? `Steps missing urlTemplate: ${missing}`
        : invalidIds
          ? `Invalid endpoint IDs: ${invalidIds}`
          : 'Configuration rejected — retrying');
      // `no_steps` ends the generation, so there is no retry to announce.
      if (result.reason === 'no_steps') return { detail };
      return { detail, badge: { label: 'Will retry', variant: 'negative' } };
    }
    default:
      return {};
  }
}

/** Convert raw events into renderable log entries */
export function buildLogEntries(events: AgentEvent[], status: string): LogEntry[] {
  const entries: LogEntry[] = [];

  let toolResultIndex = 0;
  const toolResults = events.filter((event) => event.type === 'tool-result');

  for (let i = 0; i < events.length; i += 1) {
    const event = events[i];

    if (event.type === 'progress') {
      entries.push({
        key: `progress-${i}`,
        icon: 'info',
        text: event.message ?? 'Processing...',
      });
      continue;
    }

    if (event.type === 'tool-call') {
      const matchingResult = toolResults[toolResultIndex];
      const hasResult = matchingResult && matchingResult.toolName === event.toolName;

      if (hasResult) {
        toolResultIndex += 1;
        const resultInfo = getToolResultInfo(matchingResult);
        const hasError =
          matchingResult.result &&
          typeof matchingResult.result === 'object' &&
          'error' in matchingResult.result;

        entries.push({
          key: `tool-${i}`,
          icon: matchingResult.success !== false && !hasError ? 'success' : 'error',
          text: getToolDisplayLabel(event.toolName ?? 'tool', event.args),
          detail: resultInfo.detail,
          badge: resultInfo.badge,
        });
      } else {
        entries.push({
          key: `tool-${i}`,
          icon: status === 'running' ? 'spinner' : 'info',
          text: getToolDisplayLabel(event.toolName ?? 'tool', event.args),
        });
      }
      continue;
    }

    if (event.type === 'error') {
      entries.push({
        key: `error-${i}`,
        icon: 'error',
        text: event.message ?? 'An error occurred',
      });
    }
  }

  return entries;
}
